import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addToast } from '@heroui/toast';
import { fetchActivity, revokeSession, revokeUserSessions } from '@/api/adminApi';
import { input } from '@/components/ui/design';
import UserSessionsModal from './UserSessionsModal';
import { EmptyRow, rowBorder, smallBtn, tableWrap, td, th } from './shared';

const WINDOWS = [
    { label: '10 minutes', minutes: 10 },
    { label: '30 minutes', minutes: 30 },
    { label: '24 hours', minutes: 24 * 60 },
    { label: '7 days', minutes: 7 * 24 * 60 },
    { label: '30 days', minutes: 30 * 24 * 60 },
];

export default function ActivityTab() {
    const queryClient = useQueryClient();
    const [windowMinutes, setWindowMinutes] = useState(30);
    const [sessionsEmail, setSessionsEmail] = useState<string | null>(null);
    const { data, isLoading, isError } = useQuery({
        queryKey: ['admin-activity', windowMinutes],
        queryFn: () => fetchActivity(windowMinutes),
        refetchInterval: 30_000,
        placeholderData: previous => previous,
    });
    const refresh = () => queryClient.invalidateQueries({ queryKey: ['admin-activity'] });
    const onError = (error: any) => addToast({ title: 'Could not sign out session', description: error?.response?.data?.message || error?.message || 'Please try again.', color: 'danger' });
    const revokeOne = useMutation({ mutationFn: revokeSession, onSuccess: refresh, onError });
    const revokeAll = useMutation({ mutationFn: revokeUserSessions, onSuccess: refresh, onError });
    const counts = [data?.last10m, data?.last30m, data?.last24h, data?.last7d, data?.last30d];
    const guestCounts = [data?.guests.last10m, data?.guests.last30m, data?.guests.last24h, data?.guests.last7d, data?.guests.last30d];

    return (
        <div>
            <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-6">
                <div className="rounded-[24px] bg-white p-5 ring-1 ring-black/[0.06] dark:bg-white/[0.04] dark:ring-white/10">
                    <div className="text-3xl font-bold tracking-[-0.03em] text-grey10 dark:text-white">{(data?.activeSessions ?? 0).toLocaleString()}</div>
                    <div className="mt-1 text-xs font-semibold uppercase tracking-[0.12em] text-grey40 dark:text-nightMuted">Active sessions, last 10 minutes</div>
                </div>
                {WINDOWS.map((w, i) => (
                    <div key={w.minutes} className="rounded-[24px] bg-white p-5 ring-1 ring-black/[0.06] dark:bg-white/[0.04] dark:ring-white/10">
                        <div className="text-3xl font-bold tracking-[-0.03em] text-grey10 dark:text-white">{(counts[i] ?? 0).toLocaleString()}</div>
                        <div className="mt-1 text-xs font-semibold uppercase tracking-[0.12em] text-grey40 dark:text-nightMuted">Active users, last {w.label}</div>
                    </div>
                ))}
            </div>
            <div className="mb-4 flex items-center gap-3">
                <h2 className="text-lg font-bold text-grey10 dark:text-white">Active sessions</h2>
                <select value={windowMinutes} onChange={e => setWindowMinutes(Number(e.target.value))} className={`${input} md:max-w-[180px]`} aria-label="Window">
                    {WINDOWS.map(w => <option key={w.minutes} value={w.minutes}>Last {w.label}</option>)}
                </select>
            </div>
            {isError && <p className="mb-3 text-sm text-red-500">Failed to load activity.</p>}
            <div className={tableWrap}>
                <table className="w-full min-w-[720px]">
                    <thead>
                        <tr><th className={th}>User</th><th className={th}>Device</th><th className={th}>IP</th><th className={th}>Last seen</th><th className={th} /></tr>
                    </thead>
                    <tbody>
                        {(data?.recent ?? []).map(session => (
                            <tr key={session.id} className={rowBorder}>
                                <td className={td}>{session.email ? <button type="button" className="font-semibold underline" onClick={() => setSessionsEmail(session.email!)}>{session.email}</button> : 'Unknown'}</td>
                                <td className={td}>{session.device}</td>
                                <td className={td}>{session.ip}</td>
                                <td className={`${td} whitespace-nowrap`}>{new Date(session.lastSeenAt).toLocaleString()}</td>
                                <td className={`${td} whitespace-nowrap text-right`}>
                                    <button type="button" className={smallBtn} disabled={revokeOne.isPending} onClick={() => window.confirm('Sign out this session?') && revokeOne.mutate(session.id)}>Sign out</button>
                                    {session.email && <button type="button" className={`${smallBtn} ml-2`} disabled={revokeAll.isPending} onClick={() => window.confirm(`Sign out every session for ${session.email}?`) && revokeAll.mutate(session.email!)}>Sign out all</button>}
                                </td>
                            </tr>
                        ))}
                        {(data?.recent ?? []).length === 0 && <EmptyRow colSpan={5} loading={isLoading} text="No active sessions in this window." />}
                    </tbody>
                </table>
            </div>
            <h2 className="mb-1 mt-8 text-lg font-bold text-grey10 dark:text-white">Guests</h2>
            <p className="mb-4 text-sm text-grey40 dark:text-nightMuted">Signed-out visitors, counted per browser. {WINDOWS.map((w, i) => `${(guestCounts[i] ?? 0).toLocaleString()} in ${w.label}`).join(' · ')}</p>
            <div className={tableWrap}>
                <table className="w-full min-w-[720px]">
                    <thead>
                        <tr><th className={th}>Guest</th><th className={th}>Device</th><th className={th}>IP</th><th className={th}>Pages viewed</th><th className={th}>Last seen</th></tr>
                    </thead>
                    <tbody>
                        {(data?.guests.recent ?? []).map(guest => (
                            <tr key={guest.id} className={rowBorder}>
                                <td className={td}>{guest.guestId.slice(0, 8)}</td>
                                <td className={td}>{guest.device}</td>
                                <td className={td}>{guest.ip}</td>
                                <td className={td}>{guest.views.map((v, i) => <div key={i}><span className="break-all">{v.path.replace('/eventopia/', '')}</span> <span className="text-grey40 dark:text-nightMuted">{new Date(v.at).toLocaleTimeString()}</span></div>)}</td>
                                <td className={`${td} whitespace-nowrap`}>{new Date(guest.lastSeenAt).toLocaleString()}</td>
                            </tr>
                        ))}
                        {(data?.guests.recent ?? []).length === 0 && <EmptyRow colSpan={5} loading={isLoading} text="No guest activity in this window." />}
                    </tbody>
                </table>
            </div>
            <p className="mt-3 text-xs text-grey40 dark:text-nightMuted">Showing up to 200 most recent sessions. A signed-out session is rejected on its next request (within about 30 seconds).</p>
        {sessionsEmail && <UserSessionsModal email={sessionsEmail} onClose={() => setSessionsEmail(null)} />}
        </div>
    );
}
